import React, { ReactNode, useEffect } from 'react'

import Button from '../../atoms/Button/Button'
import Icon from '../../atoms/Icon/Icon'
import Text from '../../atoms/Text/Text'
import ButtonwithSpinner from 'app/app/(client)/components/molecules/ButtonwithSpinner/ButtonwithSpinner'
import { t } from 'i18next'
import InformationBanner from '../InformationBanner/InformationBanner'

export interface ModalProps {
  id: string
  title?: string
  isOpen: boolean
  onClose: () => void
  onPrimary?: () => void
  onSecondary?: () => void
  children: ReactNode
  dismissOnBackdrop?: boolean
  disablePrimaryButton?: boolean
  loadingPrimaryButton?: boolean
  disableThirdButton?: boolean
  primaryBtnLabel?: string
  secondaryBtnLabel?: string
  className?: string
  scrollableBody?: boolean
  thirdBtnContent?: React.ReactNode
  onThird?: () => void
  size?: 'small' | 'medium' | 'large'
  alertMessage?:
    | {
        type: 'success' | 'error' | 'info' | 'warning'
        message: string | string[] | undefined
      }
    | undefined
}

const Modal: React.FC<ModalProps> = ({
  id,
  title = '',
  isOpen,
  onClose,
  onPrimary,
  onSecondary,
  children,
  dismissOnBackdrop = true,
  disablePrimaryButton = false,
  loadingPrimaryButton = false,
  disableThirdButton = false,
  primaryBtnLabel = 'Save',
  secondaryBtnLabel = 'Cancel',
  className = '',
  scrollableBody = false,
  thirdBtnContent = null,
  onThird,
  size = 'medium',
  alertMessage = undefined
}) => {
  useEffect(() => {
    if (isOpen) {
      document.body.classList.add('overflow-y-hidden')
    } else {
      document.body.classList.remove('overflow-y-hidden')
    }
    return () => {
      document.body.classList.remove('overflow-y-hidden')
    }
  }, [isOpen])

  if (!isOpen) {
    return null
  }

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget && dismissOnBackdrop) {
      onClose()
    }
  }
  const primaryBtnText = primaryBtnLabel || t('errors.modalOK') || 'Ok'
  const dismissBtnText = secondaryBtnLabel || t('errors.modalCancel') || 'Cancel'

  const sizeClasses = {
    small: 'modal-container--small',
    medium: 'modal-container--medium',
    large: 'modal-container--large'
  }

  return (
    <div className="modal-backdrop" onClick={handleBackdropClick} data-testid="modal-backdrop">
      <div id={id} className={`modal-container ${sizeClasses[size]} ${className}`} role="dialog" aria-modal="true">
        {/* Header */}
        <div className="modal-header">
          <Text as="div" className="modal-title" id={`${id}-title`}>
            {title}
          </Text>
          <Button
            id={`${id}-close-btn`}
            variant="tertiary"
            size="small"
            className="modal-close-btn btn-icon"
            onClick={onClose}
            aria-label="Close"
            disabled={loadingPrimaryButton}
          >
            <Icon name="icon-close-cross" className="text-black text-24px" />
          </Button>
        </div>

        {/* Body */}
        <div className={`modal-body mt-4 ${scrollableBody ? 'modal-body--scrollable' : ''}`}>{children}</div>

        {/* Footer */}
        <div className={`modal-footer`}>
          {alertMessage &&
            alertMessage.message &&
            (Array.isArray(alertMessage.message) ? (
              <InformationBanner
                id="modal-warning-message"
                message={
                  <ul className="list-none list-inside">
                    {alertMessage.message.map((msg, idx) => (
                      <li key={idx}>{msg}</li>
                    ))}
                  </ul>
                }
                type={alertMessage.type}
                className="mb-2"
              />
            ) : (
              <InformationBanner
                id="modal-warning-message"
                message={alertMessage.message as string}
                type={alertMessage.type}
                className="mb-2 break-all"
              />
            ))}
          <div className={`flex flex-col md:flex-row ${thirdBtnContent ? 'justify-between' : 'justify-end'}`}>
            <div className={`modal-footer__third-btn-container`}>
              {onThird && (
                <Button
                  id="modal-third-btn"
                  className="btn !min-w-20"
                  variant="tertiary"
                  size="xsmall"
                  onClick={onThird}
                  disabled={loadingPrimaryButton || disableThirdButton}
                >
                  {thirdBtnContent}
                </Button>
              )}
            </div>
            <div className="modal-footer__actions">
              {onSecondary && (
                <Button
                  id="modal-secondary-btn"
                  className="btn w-full md:w-fit !min-w-20"
                  variant="secondary"
                  size="xsmall"
                  onClick={onSecondary}
                  disabled={loadingPrimaryButton}
                >
                  {dismissBtnText}
                </Button>
              )}
              {onPrimary && (
                <ButtonwithSpinner
                  id="modal-primary-btn"
                  variant="primary"
                  className="btn w-full md:w-fit !min-w-20"
                  size="xsmall"
                  onClick={onPrimary}
                  disabled={disablePrimaryButton}
                  loading={loadingPrimaryButton}
                >
                  {primaryBtnText}
                </ButtonwithSpinner>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default Modal
